import React, { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Chip,
    Stack
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningIcon from "@mui/icons-material/Warning";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import FolderIcon from "@mui/icons-material/Folder";
import { useAuth } from "@clerk/clerk-react";
import { callBackend, formatBackendError, formatNetworkError } from "../api";

const LessonCard = ({ lesson, searchField, searchText, googleDriveLink, handleOpen, heygenApiKey, onLessonChanged }) => {
    const { getToken } = useAuth();
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(lesson.status);
    const [isDeleted, setIsDeleted] = useState(lesson.isDeleted);
    const [isForceDeleted, setIsForceDeleted] = useState(lesson.isForceDeleted);
    const [isCloudflareVideoNotFound, setIsCloudflareVideoNotFound] = useState(lesson.isCloudflareVideoNotFound || false);
    const [googleDriveLinkAlternative, setGoogleDriveLinkAlternative] = useState(lesson.googleDriveLink || null);
    const [incompleteLessons, setIncompleteLessons] = useState([]);
    const [uploadError, setUploadError] = useState(false);
    const [videoLongerThan30Minutes, setVideoLongerThan30Minutes] = useState(lesson.videoLongerThan30Minutes || false);
    // Languages whose Cloudflare copy exists but has not been handed to TRW yet, and languages we
    // are still waiting on Heygen for. The lesson reads as in-progress either way, but they are
    // completely different situations: one finishes by itself, the other needs the operator to go
    // to Heygen. Sending a duplicate batch to Heygen for a lesson that was merely encoding costs
    // real money, so these must never look alike.
    const [cloudflarePending, setCloudflarePending] = useState(lesson.cloudflarePending || []);
    const [awaitingHeygen, setAwaitingHeygen] = useState(lesson.awaitingHeygen || []);
    // COMPATIBILITY SHIM, remove one release after backend 1.0.55. The backend deploys via GitHub
    // Actions and this card via Vercel, so they cannot land together; an older backend sends only
    // this field, and without the fallback the encoding state below would be unreachable.
    const [cloudflareBlockedAt, setCloudflareBlockedAt] = useState(lesson.cloudflareBlockedAt || null);
    // The lesson exists but its Google Drive copy does not yet: Cloudflare is still making the MP4
    // the copy is taken from, and the backend's sweep finishes it by itself (backend 1.0.62+).
    const [preparingSource, setPreparingSource] = useState(!!lesson.preparingSource);
    const preparingTooLong =
        preparingSource && !!lesson.preparingSince && Date.now() - new Date(lesson.preparingSince).getTime() > 30 * 60 * 1000;

    // The backend now changes this lesson WITHOUT the operator doing anything: the Cloudflare
    // sweep promotes copies, publishes to TRW and deletes the Drive source on its own schedule.
    // Every piece of state above is seeded from props exactly once (the card is keyed by lessonId,
    // so React reuses it forever), which was safe only while the server could not move on its own.
    // Re-sync whenever Home refetches, keyed on the server values rather than on object identity
    // so a no-op refetch cannot clobber a fresher /update-status response.
    const serverPending = JSON.stringify(lesson.cloudflarePending || []);
    const serverAwaiting = JSON.stringify(lesson.awaitingHeygen || []);
    useEffect(() => {
        setStatus(lesson.status);
        setIsDeleted(lesson.isDeleted);
        setIsForceDeleted(lesson.isForceDeleted);
        setIsCloudflareVideoNotFound(lesson.isCloudflareVideoNotFound || false);
        setCloudflarePending(JSON.parse(serverPending));
        setAwaitingHeygen(JSON.parse(serverAwaiting));
        setCloudflareBlockedAt(lesson.cloudflareBlockedAt || null);
        setPreparingSource(!!lesson.preparingSource);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lesson.status, lesson.isDeleted, lesson.isForceDeleted, lesson.isCloudflareVideoNotFound, serverPending, serverAwaiting, lesson.cloudflareBlockedAt, lesson.preparingSource]);

    // True when everything still outstanding is waiting on CLOUDFLARE, not on Heygen. The second
    // clause is the compatibility fallback for an older backend.
    const waitingOnCloudflare =
        (cloudflarePending.length > 0 && awaitingHeygen.length === 0) ||
        (cloudflarePending.length === 0 && !!cloudflareBlockedAt);

    // New State for Delete Dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // New State for Folder Dialog
    const [folderDialogOpen, setFolderDialogOpen] = useState(false);
    const [folderIds, setFolderIds] = useState([]);
    const [savedFolderIds, setSavedFolderIds] = useState(null);
    const [newFolderId, setNewFolderId] = useState("");
    const [savingFolders, setSavingFolders] = useState(false);
    const [shrinkConfirmOpen, setShrinkConfirmOpen] = useState(false);

    // Every snackbar is prefixed with this so the operator knows which card spoke.
    const lessonLabel = `${lesson.lesson || "Lesson"} (${lesson.lessonId})`;

    // Single source of truth for what the backend holds. The `lesson` prop is only
    // refreshed when Home refetches, so right after a save it still carries the old
    // list - an in-session save (`savedFolderIds`, null until this card has one) wins.
    const storedFolderIds = savedFolderIds !== null
        ? savedFolderIds
        : (Array.isArray(lesson.folderIds) ? lesson.folderIds : []);

    const updateLessonStatus = async () => {
        setLoading(true);
        try {
            const token = await getToken();
            const result = await callBackend("/update-status", {
                token,
                method: "POST",
                body: {
                    lessonId: lesson.lessonId,
                    campusId: lesson.campusId,
                    videoId: lesson.videoId,
                    heygenApiKey: heygenApiKey
                },
            });

            // HTTP error, text/plain 500, or `{ error }` with HTTP 200 - all land here.
            if (!result.ok) {
                handleOpen(formatBackendError(result, lessonLabel));
                return;
            }

            const data = result.data || {};
            const previousStatus = status;

            setStatus(data.status);
            setIsDeleted(data.isDeleted);
            setIsForceDeleted(data.isForceDeleted);
            setIsCloudflareVideoNotFound(data.isCloudflareVideoNotFound || false);
            setGoogleDriveLinkAlternative(data.googleDriveLink);
            setUploadError(data.uploadError);
            setVideoLongerThan30Minutes(data.videoLongerThan30Minutes || false);
            // Guarded per field, not merged: /force-delete-lesson and the Branch A early exits do
            // not send these keys at all, and an unguarded `data.x || []` would wipe a live list.
            if (data.cloudflarePending !== undefined) setCloudflarePending(data.cloudflarePending || []);
            if (data.awaitingHeygen !== undefined) setAwaitingHeygen(data.awaitingHeygen || []);
            if (data.cloudflareBlockedAt !== undefined) setCloudflareBlockedAt(data.cloudflareBlockedAt);
            setPreparingSource(!!data.preparingSource);

            // Unconditional: gating this on uploadError left a stale list on screen.
            setIncompleteLessons(Array.isArray(data.incompleteLanguages) ? data.incompleteLanguages : []);

            // `message` is "" on several success paths, so the status drives the outcome.
            let message;
            if (data.uploadError) {
                message = "This lesson needs manual fix in the backend.";
            } else {
                message = data.message || `Status: ${data.status || "unknown"}.`;

                if (data.incompleteLanguages && data.incompleteLanguages.length > 0) {
                    message += ` Incomplete languages: ${data.incompleteLanguages.join(", ")}`;
                }
            }

            if (result.requestId) {
                message += ` [rid=${result.requestId}]`;
            }

            handleOpen(`${lessonLabel}: ${message}`);

            // Unconditional. Gating this on a status CHANGE meant a run that queued twelve copies
            // reported "in-progress", matched the previous value, and never refreshed - so Home's
            // pending counter froze and its polling never learned there was anything to watch.
            if (onLessonChanged && data.status) {
                onLessonChanged();
            }
            void previousStatus;
        } catch (err) {
            console.error("Failed to update lesson status", err);
            handleOpen(formatNetworkError(err, lessonLabel));
        } finally {
            setLoading(false);
        }
    };

    // Force Delete Handler
    const handleForceDelete = async () => {
        setDeleting(true);
        try {
            const token = await getToken();
            const result = await callBackend("/force-delete-lesson", {
                token,
                method: "POST",
                body: { lessonId: lesson.lessonId },
            });

            setDeleting(false);
            setDeleteDialogOpen(false);

            if (!result.ok) {
                handleOpen(formatBackendError(result, `${lessonLabel} force delete failed`));
                return;
            }

            const data = result.data || {};

            // The backend swallows Google Drive failures and still answers 200, so
            // only flip local state when it actually confirms the deletion.
            if (data.isForceDeleted === true) {
                setIsForceDeleted(true);
                setIsCloudflareVideoNotFound(data.isCloudflareVideoNotFound || false);
                handleOpen(`${lessonLabel}: force deleted from Google Drive.${result.requestId ? ` [rid=${result.requestId}]` : ""}`);

                if (onLessonChanged) {
                    onLessonChanged();
                }
            } else {
                handleOpen(`${lessonLabel}: backend did not confirm the delete (isForceDeleted=${String(data.isForceDeleted)}). Check the pod logs.${result.requestId ? ` [rid=${result.requestId}]` : ""}`);
            }
        } catch (err) {
            setDeleting(false);
            setDeleteDialogOpen(false);
            console.error("Error deleting lesson:", err);
            handleOpen(formatNetworkError(err, `${lessonLabel} force delete failed`));
        }
    };

    // Open Folder Dialog Handler - seeds from what is already stored on the lesson.
    // `/lesson/set-folders` is a full replace, so starting from an empty list used
    // to silently wipe folder IDs saved in an earlier session. Seeding from
    // `storedFolderIds` also keeps a save made in this session if the parent refetch
    // is delayed or fails.
    const handleOpenFolderDialog = () => {
        const existing = storedFolderIds;
        setFolderIds([...existing]);
        setSavedFolderIds(existing);
        setNewFolderId("");
        setFolderDialogOpen(true);
    };

    // Save Folder IDs Handler
    const saveFolderIds = async () => {
        setShrinkConfirmOpen(false);
        setSavingFolders(true);
        try {
            const token = await getToken();
            const result = await callBackend("/lesson/set-folders", {
                token,
                method: "POST",
                body: {
                    lessonId: lesson.lessonId,
                    folderIds: folderIds
                },
            });

            if (result.ok && result.data && result.data.success) {
                const saved = Array.isArray(result.data.folderIds) ? result.data.folderIds : folderIds;
                setSavedFolderIds(saved);
                handleOpen(`${lessonLabel}: folder IDs updated (${saved.length}): ${saved.join(", ")}`);
                setFolderDialogOpen(false);

                // Home still holds the pre-save `lesson`; without this refresh the next
                // dialog open would reseed from a stale prop and a save would wipe the
                // IDs this one just stored.
                if (onLessonChanged) {
                    onLessonChanged();
                }
            } else if (result.ok) {
                handleOpen(`${lessonLabel}: backend did not confirm the folder update. Check the pod logs.${result.requestId ? ` [rid=${result.requestId}]` : ""}`);
            } else {
                handleOpen(formatBackendError(result, `${lessonLabel} folder update failed`));
            }
        } catch (err) {
            console.error("Error updating folder IDs:", err);
            handleOpen(formatNetworkError(err, `${lessonLabel} folder update failed`));
        } finally {
            setSavingFolders(false);
        }
    };

    const handleSaveFolderIds = () => {
        // Saving fewer IDs than are stored destroys the missing ones - confirm first.
        if (folderIds.length < storedFolderIds.length) {
            setShrinkConfirmOpen(true);
            return;
        }

        saveFolderIds();
    };

    // Add Folder ID Handler
    const handleAddFolderId = () => {
        if (newFolderId.trim() && !folderIds.includes(newFolderId.trim())) {
            setFolderIds([...folderIds, newFolderId.trim()]);
            setNewFolderId("");
        }
    };

    // Remove Folder ID Handler
    const handleRemoveFolderId = (idToRemove) => {
        setFolderIds(folderIds.filter(id => id !== idToRemove));
    };

    if (!lesson[searchField]?.toLowerCase().includes(searchText.toLowerCase())) {
        return null;
    }

    const copyLinkAndOpenHeygen = () => {
        const heygenLink = "https://app.heygen.com/apps/translate";

        const link = googleDriveLink || googleDriveLinkAlternative;
        // Never copy a placeholder: a lesson whose Drive copy is still being made has no link yet.
        if (!link || !/^https?:\/\//.test(link)) {
            handleOpen(`${lessonLabel}: The Google Drive copy is not ready yet - wait for this card to update.`);
            return;
        }

        // Copy to clipboard
        navigator.clipboard.writeText(link)
            .then(() => {
                window.open(heygenLink, "_blank", "noopener,noreferrer");
            })
            .catch(err => console.error("Failed to copy text: ", err));
    }

    // const forceComplete = async () => {
    //     setLoading(true);
    //     try {
    //         const token = await getToken();
    //
    //         const response = await fetch(
    //             `${process.env.REACT_APP_BACKEND_URL}/force-complete-lesson`,
    //             {
    //                 method: "POST",
    //                 headers: {
    //                     "Content-Type": "application/json",
    //                     Authorization: `Bearer ${token}`,
    //                 },
    //                 body: JSON.stringify({
    //                     lessonId: lesson.lessonId,
    //                     lessonTitle: lesson.lesson // Assuming lesson object contains the lesson title in `lesson.lesson`
    //                 }),
    //             }
    //         );
    //
    //     } catch (err) {
    //         console.error(err);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    return (
        <Card sx={{ mt: 2, position: "relative" }}>
            {/* Icons at top right */}
            <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, display: "flex", gap: 1 }}>
                {/* Folder Icon for in-progress lessons */}
                {status === "in-progress" && !videoLongerThan30Minutes && (
                    <IconButton
                        size="small"
                        onClick={handleOpenFolderDialog}
                        aria-label="manage folders"
                        title={`Manage Folders (${storedFolderIds.length} saved)`}
                    >
                        <FolderIcon color="primary" />
                    </IconButton>
                )}
                
                {/* Bin Icon */}
                {status !== "todo" && !isForceDeleted && !isDeleted && !videoLongerThan30Minutes && !isCloudflareVideoNotFound && (
                    <IconButton
                        size="small"
                        onClick={() => setDeleteDialogOpen(true)}
                        disabled={deleting}
                        aria-label="delete lesson"
                    >
                        <DeleteOutlineIcon color="error" />
                    </IconButton>
                )}
            </Box>

            <CardContent>
                <Typography variant="h6">{lesson.lesson}</Typography>
                <Typography variant="body2" color="text.secondary">
                    Module: {lesson.module}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Campus: {lesson.campusName}
                </Typography>
                {incompleteLessons.length > 0 && (
                    <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                        Incomplete lessons: {incompleteLessons.join(", ")}
                    </Typography>
                )}
                {uploadError && (
                    <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                        This lesson needs manual fix in the backend.
                    </Typography>
                )}

                <Box mt={1}>
                    {status === "todo" && (
                        <>
                            <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                                <Box display="flex" alignItems="center">
                                    <SendOutlinedIcon sx={{ mr: 1 }} />
                                    Awaiting Heygen
                                </Box>
                            </Typography>
                            <Button
                                variant="contained"
                                color="info"
                                size="small"
                                onClick={updateLessonStatus}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : "Send to Heygen"}
                            </Button>
                        </>
                    )}

                    {status === "in-progress" && (
                        <>
                            {/* Waiting on Cloudflare is its own state. It looks like "in-progress"
                                to /fetch because every language IS marked completed - the copies
                                exist, they are just still encoding - so without this branch the
                                operator sees the same orange "Translation is Done" button they
                                just pressed and reasonably reads the refusal as a failure. */}
                            {preparingSource && !isForceDeleted ? (
                                <>
                                    <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                                        <Box display="flex" alignItems="center">
                                            <CloudSyncIcon sx={{ mr: 1 }} />
                                            Preparing the video
                                        </Box>
                                    </Typography>

                                    {preparingTooLong ? (
                                        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                                            This has been preparing for over 30 minutes, which is unusual. Press
                                            "Check now" to retry; if it keeps failing, use the bin icon to abandon the
                                            lesson.
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            <strong>Nothing is wrong and there is nothing to do.</strong> Cloudflare is
                                            preparing the video file, and it is copied to Google Drive automatically -
                                            usually within a minute or two. This page refreshes on its own and the
                                            "Copy link and Go to Heygen" button appears here when it is ready.
                                        </Typography>
                                    )}

                                    <Button
                                        variant="outlined"
                                        color="info"
                                        size="small"
                                        onClick={updateLessonStatus}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={20} color="inherit" /> : "Check now"}
                                    </Button>
                                </>
                            ) : waitingOnCloudflare && !isForceDeleted && !isCloudflareVideoNotFound ? (
                                <>
                                    <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                                        <Box display="flex" alignItems="center">
                                            <CloudSyncIcon sx={{ mr: 1 }} />
                                            Translated - Cloudflare is encoding
                                        </Box>
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        <strong>Nothing is wrong and there is nothing to do.</strong> All 12
                                        translations came back from Heygen and are in Cloudflare, which is still
                                        encoding them{cloudflarePending.length > 0 && cloudflarePending.length < 12
                                            ? ` (${cloudflarePending.length} left: ${cloudflarePending.join(", ")})`
                                            : ""}. This lesson publishes itself as soon as they are ready -
                                        usually a few minutes, occasionally longer - and the Google Drive source
                                        is kept until it does. This page refreshes on its own.
                                        <strong> Do not go back to Heygen</strong>; that would pay for a second
                                        translation of a lesson that is already done.
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        color="info"
                                        size="small"
                                        onClick={updateLessonStatus}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={20} color="inherit" /> : "Check now"}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                                        <Box display="flex" alignItems="center">
                                            <InfoOutlinedIcon sx={{ mr: 1 }} />
                                            {isForceDeleted ? "Video force deleted" : isCloudflareVideoNotFound ? "Video file was not found, skipped" : "Ready for Translation"}

                                        </Box>
                                    </Typography>

                                    {cloudflarePending.length > 0 && !isForceDeleted && !isCloudflareVideoNotFound && (
                                        <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                                            {cloudflarePending.length} translation(s) are already encoding in
                                            Cloudflare ({cloudflarePending.join(", ")}) and will publish
                                            automatically. Only re-send the languages still listed above.
                                        </Typography>
                                    )}

                                    {!isForceDeleted && !isCloudflareVideoNotFound && (
                                        <>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                size="small"
                                                sx={{ mr: 2 }}
                                                onClick={copyLinkAndOpenHeygen}
                                            >
                                                Copy link and Go to Heygen
                                            </Button>


                                            <Button
                                                variant="contained"
                                                color="warning"
                                                size="small"
                                                onClick={updateLessonStatus}
                                                disabled={loading}
                                            >
                                                {loading ? <CircularProgress size={20} color="inherit" /> : "Translation is Done"}
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {status === "completed" && (
                        <>
                            {videoLongerThan30Minutes ? (
                                <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                                    <Box display="flex" alignItems="center">
                                        <WarningIcon sx={{ mr: 1 }} />
                                        Video Too Long (>30 minutes)
                                    </Box>
                                </Typography>
                            ) : isCloudflareVideoNotFound ? (
                                <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                                    <Box display="flex" alignItems="center">
                                        <InfoOutlinedIcon sx={{ mr: 1 }} />
                                        Video file was not found, skipped
                                    </Box>
                                </Typography>
                            ) : (
                                <>
                                    <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                                        <Box display="flex" alignItems="center">
                                            <CheckCircleOutlineOutlinedIcon sx={{ mr: 1 }} />
                                            {isDeleted ? "Completed and cleaned up from Google Drive" : "Completed"}
                                        </Box>
                                    </Typography>

                                    {!isDeleted && (
                                        <>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                onClick={updateLessonStatus}
                                                disabled={loading}
                                            >
                                                {loading ? <CircularProgress size={20} color="inherit" /> : "Cleanup video from Google Drive"}
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Box>
            </CardContent>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Force Delete Lesson</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {preparingSource
                            ? "Are you sure you want to abandon this lesson? Its video was never copied to Google Drive, so nothing is deleted - but the lesson will not be translated."
                            : "Are you sure you want to force delete this lesson? This will remove the video from Google Drive and cannot be undone."}
                    </DialogContentText>
                    {status !== "completed" && !preparingSource && (
                        <DialogContentText sx={{ mt: 2, color: "error.main", fontWeight: "bold" }}>
                            This lesson has not been published to TRW yet, so the Google Drive file is the only
                            remaining copy of the source video. Deleting it is final.
                            {cloudflarePending.length > 0 && (
                                <> It also cancels the automatic publish for the {cloudflarePending.length} translation(s)
                                still in Cloudflare ({cloudflarePending.join(", ")}); they will never reach TRW.</>
                            )}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary" disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleForceDelete}
                        color="error"
                        disabled={deleting}
                        variant="contained"
                    >
                        {deleting ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Folder Management Dialog */}
            <Dialog 
                open={folderDialogOpen} 
                onClose={() => setFolderDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Manage Folder IDs</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Add or remove folder IDs for lesson: {lesson.lesson}
                    </DialogContentText>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Currently stored on this lesson: {storedFolderIds.length} folder ID(s)
                        {storedFolderIds.length > 0 ? ` (${storedFolderIds.join(", ")})` : ""}. Saving replaces the stored list entirely.
                    </Typography>

                    {folderIds.length === 0 && (
                        <Typography variant="body2" color="warning.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                            With no folder IDs the scan has nowhere to look, and the save will be refused.
                            Add at least one.
                        </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        There is no right number of folder IDs - Heygen caps one job at 10 languages, so the
                        12 languages go out as several batches, and those batches may all land in one folder
                        or be spread across several. What matters is that every folder a batch went into is
                        listed here: a folder that is missing is invisible to the scan. Record them all, then
                        click "Translation is Done". If a language stays listed as waiting on Heygen once Heygen
                        has finished rendering it, a batch is probably sitting in a folder that is not on this
                        list. A lesson showing "Cloudflare is encoding" is a different thing entirely - that one
                        needs no folder and no action, it finishes by itself.
                    </Typography>

                    {/* Add new folder ID */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="New Folder ID"
                            value={newFolderId}
                            onChange={(e) => setNewFolderId(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddFolderId();
                                }
                            }}
                        />
                        <Button 
                            variant="contained" 
                            onClick={handleAddFolderId}
                            disabled={!newFolderId.trim()}
                            size="small"
                        >
                            Add
                        </Button>
                    </Box>

                    {/* Display current folder IDs */}
                    {folderIds.length > 0 ? (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Current Folder IDs:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                                {folderIds.map((folderId, index) => (
                                    <Chip
                                        key={index}
                                        label={folderId}
                                        onDelete={() => handleRemoveFolderId(folderId)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No folder IDs assigned yet.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setFolderDialogOpen(false)} 
                        color="primary" 
                        disabled={savingFolders}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveFolderIds}
                        color="primary"
                        disabled={savingFolders}
                        variant="contained"
                    >
                        {savingFolders ? <CircularProgress size={20} color="inherit" /> : "Save Folder IDs"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation before a save that would drop stored folder IDs */}
            <Dialog open={shrinkConfirmOpen} onClose={() => setShrinkConfirmOpen(false)}>
                <DialogTitle>Remove stored folder IDs?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This lesson currently has {storedFolderIds.length} folder ID(s) stored and you are about to
                        save {folderIds.length}. Saving replaces the stored list, so
                        {" "}{storedFolderIds.filter((id) => !folderIds.includes(id)).join(", ") || "the missing IDs"}
                        {" "}will be lost and the lesson may never reach 12 translated videos.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShrinkConfirmOpen(false)} color="primary" disabled={savingFolders}>
                        Cancel
                    </Button>
                    <Button onClick={saveFolderIds} color="error" variant="contained" disabled={savingFolders}>
                        {savingFolders ? <CircularProgress size={20} color="inherit" /> : "Save anyway"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default LessonCard;